import { Link, Navigate, useParams } from "react-router";
import { WhoCanUsePanel } from "@/components/journey/WhoCanUsePanel";
import { Button } from "@/components/ui/button";
import { brandHasTool, findModule } from "@/lib/modules";
import { useAccountWorkspace } from "@/lib/account-workspace";
import {
  defaultSettingsSubsection,
  productSettingsChildren,
  productSettingsSectionTitle,
} from "@/lib/product-settings-nav";

/** 子应用独立左侧导航 + 内容（启用态来自 /api/modules；设置为大区可挂成员等子页） */
export default function ProductAppPage() {
  const { brandId, appKey, section = "overview", subsection } = useParams();
  const { accountId, brands, modules } = useAccountWorkspace();
  const brand = brands.find((item) => String(item.id) === brandId);
  const app = findModule(modules, appKey);
  const brandToolEnabled = brandHasTool(brand, appKey);

  if (!app || !app.enabled) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">子应用不存在或账户未订阅。</p>
        {brandId && (
          <Link
            to={`/accounts/${accountId}/brands/${brandId}`}
            className="text-sm text-primary-readable hover:underline"
          >
            返回品牌
          </Link>
        )}
      </div>
    );
  }

  if (!brandToolEnabled) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-card px-6 py-10 text-center">
        <p className="text-sm font-medium text-foreground">
          本品牌尚未开通{app.name}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {brand ? `「${brand.name}」` : "该品牌"}需先在品牌面板开通此工具。
        </p>
        {brandId ? (
          <Button asChild className="mt-4" size="sm">
            <Link to={`/accounts/${accountId}/brands/${brandId}`}>前往品牌开通</Link>
          </Button>
        ) : null}
      </div>
    );
  }

  const sectionTitle = app.nav.find((item) => item.key === section)?.title ?? section;
  const settingsChildren = productSettingsChildren(appKey);
  const settingsChild = settingsChildren.find((item) => item.key === subsection);
  const isSettingsSection = section === "settings";

  // 有设置子导航时：裸 /settings 落到默认子项（Journey → 旅程配置）
  if (isSettingsSection && !subsection) {
    const fallback = defaultSettingsSubsection(appKey);
    if (fallback && accountId && brandId) {
      return (
        <Navigate
          to={`/accounts/${accountId}/brands/${brandId}/apps/${appKey}/settings/${fallback}`}
          replace
        />
      );
    }
  }

  const settingsSectionLabel =
    isSettingsSection
      ? productSettingsSectionTitle(appKey, app.name)
      : sectionTitle;
  const pageTitle = settingsChild
    ? `${app.name} · ${settingsSectionLabel} · ${settingsChild.title}`
    : `${app.name} · ${isSettingsSection ? settingsSectionLabel : sectionTitle}`;

  const isJourneySettingsMembers =
    appKey === "journey" && isSettingsSection && subsection === "members";

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <div className="page-eyebrow">子应用壳</div>
        <h1 className="text-2xl font-semibold tracking-tight">{pageTitle}</h1>
        <p className="text-sm text-muted-foreground">
          当前品牌：{brand?.name ?? `#${brandId}`}
        </p>
      </div>

      {isJourneySettingsMembers && accountId && brandId ? (
        <WhoCanUsePanel
          accountId={accountId}
          brandId={brandId}
          product="journey"
          productName={app.name}
          brandName={brand?.name}
        />
      ) : (
        <div className="rounded-lg border border-dashed border-border bg-card px-6 py-16 text-center">
          <p className="text-sm text-muted-foreground">
            这里是 <span className="font-medium text-foreground">{app.name}</span> /
            <span className="font-medium text-foreground">
              {" "}
              {settingsChild ? `${settingsSectionLabel} · ${settingsChild.title}` : settingsSectionLabel}
            </span>{" "}
            的业务面板占位。
          </p>
          <p className="mt-2 text-xs text-muted-foreground">真实业务能力将在后续里程碑接入。</p>
        </div>
      )}
    </div>
  );
}
